import React, { FC, Fragment, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { differenceBy } from 'lodash';

import { connectionPopup } from 'helpers';
import { Feature, GetSharePreviewInfoQuery } from 'api';
import { useClient, useContent, useCurrentUser, usePrevious, useSocialConnections } from 'hooks';

import { INetwork, IShare } from '../types';
import { NETWORKS } from '../constants';
import { ActiveAuth, ActiveUnauth, Inactive } from '../components';
import { isPhotoOnly } from '../../content/helpers';

interface IProps {
  onConnect: () => void;
  previewInfo?: GetSharePreviewInfoQuery['getSharePreviewInfo'];
}

const useStyles = makeStyles(theme => ({
  item: {
    padding: '12px 16px 16px',
    marginBottom: theme.spacing(3),
  },
}));

const Networks: FC<IProps> = ({ previewInfo, onConnect }) => {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const client = useClient();
  const connections = useSocialConnections();
  const prevConnections = usePrevious(connections);
  const content = useContent();
  const { setFieldValue } = useFormikContext<IShare>();

  useEffect(() => {
    if (!connections.length) {
      return;
    }

    const [addedNetwork] = differenceBy(connections, prevConnections, 'id');

    setFieldValue(`${addedNetwork.provider}.network_id`, addedNetwork.id);
    setFieldValue(`${addedNetwork.provider}.active`, true);

    onConnect();
  }, [connections.length]);

  function handleConnect({ provider }: INetwork) {
    connectionPopup(
      currentUser.id,
      provider,
      (client.features || []).includes(Feature.LegacyAuthDomain),
    );
  }

  function renderInactiveNetwork(network: INetwork) {
    return (
      <Paper key={network.provider} className={classes.item}>
        <Inactive network={network} onConnect={() => handleConnect(network)} />
      </Paper>
    );
  }

  function renderActiveNetwork(network: INetwork) {
    const accounts = connections.filter(account => account.provider === network.provider);
    const isAuthed = accounts.length && network.auth;

    if (isAuthed) {
      return (
        <Paper key={network.provider} className={classes.item}>
          <ActiveAuth
            network={network}
            accounts={accounts}
            onConnect={handleConnect}
            previewInfo={previewInfo}
          />
        </Paper>
      );
    }

    return (
      <Paper key={network.provider} className={classes.item}>
        <ActiveUnauth network={network} />
      </Paper>
    );
  }

  function renderNetwork(network: INetwork) {
    const hasConnection = connections.some(conn => conn.provider === network.provider);
    const isActive = hasConnection || !network.auth;

    return isActive ? renderActiveNetwork(network) : renderInactiveNetwork(network);
  }

  if (isPhotoOnly(content)) {
    NETWORKS.splice(2, 1);
  }

  return <Fragment>{NETWORKS.map(renderNetwork)}</Fragment>;
};

export default Networks;
